import React, { useCallback } from 'react';
import Localize from 'react-intl-universal';

import { ArrowBack, ArrowForward, DoubleArrow } from '@mui/icons-material';
import { MenuItem, MenuList, IconButton, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

const StyledMenuList = styled(MenuList)(() => ({
  display: 'flex',
  justifyContent: 'center',
  paddingTop: 0,
  paddingBottom: 0,
  marginBottom: '1rem',
  height: '2.7rem'
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  background: theme.palette.background.primary,
  '&.Mui-disabled': {
    opacity: '0.80'
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.grey[300]
  }
}));

const AgendaTimelineMenu = ({
  menuItems,
  firstMenuItem,
  lastMenuItem,
  activeItem,
  activeStep,
  setActiveStep,
  updateMenuIndexes
}) => {
  const visibleMenuItems = menuItems.slice(firstMenuItem, lastMenuItem);
  const disableFirstArrow = activeStep === 0;
  const disableLastArrow = activeStep === menuItems.length - 1;

  const onDateClick = (menuItem) => {
    const step = menuItems.indexOf(menuItem);
    if (step !== -1) {
      setActiveStep(step);
    }
  };

  const onMenuNavigationItem = useCallback(
    (action) => {
      updateMenuIndexes(action);
    },
    [updateMenuIndexes]
  );

  const handleKeyDown = (event, item) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (item.type === 'date') {
          onDateClick(item.value);
        } else if (item.type === 'nav') {
          onMenuNavigationItem(item.value);
        }
        break;

      case 'ArrowLeft':
        event.preventDefault();
        onMenuNavigationItem('backward');
        break;

      case 'ArrowRight':
        event.preventDefault();
        onMenuNavigationItem('forward');
        break;

      case 'Home':
        event.preventDefault();
        onMenuNavigationItem('first');
        break;

      case 'End':
        event.preventDefault();
        onMenuNavigationItem('last');
        break;

      default:
        break;
    }
  };

  let items = [];

  if (visibleMenuItems?.length && activeItem) {
    // Navigation arrows
    items.push(
      <StyledMenuItem
        key='first'
        role='button'
        aria-label={Localize.get('App.GoToFirstDate')}
        disabled={disableFirstArrow}
        onClick={() => onMenuNavigationItem('first')}
        onKeyDown={(e) => handleKeyDown(e, { type: 'nav', value: 'first' })}
      >
        <IconButton tabIndex={0} disabled={disableFirstArrow}>
          <DoubleArrow sx={{ transform: 'rotate(180deg)' }} />
        </IconButton>
      </StyledMenuItem>
    );

    items.push(
      <StyledMenuItem
        key='backward'
        role='button'
        aria-label={Localize.get('App.GoToPreviousDate')}
        disabled={disableFirstArrow}
        onClick={() => onMenuNavigationItem('backward')}
        onKeyDown={(e) => handleKeyDown(e, { type: 'nav', value: 'backward' })}
      >
        <IconButton tabIndex={0} disabled={disableFirstArrow}>
          <ArrowBack />
        </IconButton>
      </StyledMenuItem>
    );

    // Dates
    visibleMenuItems.forEach((menuItem) => {
      items.push(
        <StyledMenuItem
          key={menuItem}
          tabIndex={0}
          role='button'
          aria-current={menuItem === activeItem ? 'date' : undefined}
          selected={menuItem === activeItem}
          onClick={() => onDateClick(menuItem)}
          onKeyDown={(e) => handleKeyDown(e, { type: 'date', value: menuItem })}
        >
          <Typography>{menuItem || Localize.get('App.invalidDate')}</Typography>
        </StyledMenuItem>
      );
    });

    // Navigation arrows
    items.push(
      <StyledMenuItem
        key='forward'
        role='button'
        aria-label={Localize.get('App.GoToNextDate')}
        onClick={() => onMenuNavigationItem('forward')}
        onKeyDown={(e) => handleKeyDown(e, { type: 'nav', value: 'forward' })}
        disabled={disableLastArrow}
      >
        <IconButton disabled={disableLastArrow} tabIndex={0}>
          <ArrowForward />
        </IconButton>
      </StyledMenuItem>
    );

    items.push(
      <StyledMenuItem
        key='last'
        role='button'
        aria-label={Localize.get('App.GoToLastDate')}
        disabled={disableLastArrow}
        onClick={() => onMenuNavigationItem('last')}
        onKeyDown={(e) => handleKeyDown(e, { type: 'nav', value: 'last' })}
      >
        <IconButton tabIndex={0} disabled={disableLastArrow}>
          <DoubleArrow />
        </IconButton>
      </StyledMenuItem>
    );
  }

  return (
    <StyledMenuList
      data-testid='agendaMenu'
      role='menubar'
      aria-label={Localize.get('App.AgendaTimelineNavigation')}
    >
      <Box sx={{ display: 'flex' }}>{items}</Box>
    </StyledMenuList>
  );
};

export default AgendaTimelineMenu;
